import { useEffect, useState } from 'react';
import ErrorBanner from '../../components/ErrorBanner';
import {
  applyFeedbackCorrection,
  deleteFeedback,
  listFeedback,
  updateFeedbackStatus,
} from '../../api/feedback';
import { listLanguages } from '../../api/languages';

const CATEGORY_LABELS = {
  lyrics_correction: 'Lyrics correction',
  song_feedback: 'Song feedback',
  app_issue: 'App issue',
  suggestion: 'Suggestion',
};

const STATUSES = [
  { value: 'new', label: 'New' },
  { value: 'in_progress', label: 'In progress' },
  { value: 'resolved', label: 'Resolved' },
];

const STATUS_STYLES = {
  new: 'bg-amber-100 text-amber-800',
  in_progress: 'bg-blue-100 text-blue-800',
  resolved: 'bg-green-100 text-green-800',
};

function ContextLine({ context, original }) {
  const index = context.indexOf(original);
  if (index === -1) return context;
  return (
    <>
      {context.slice(0, index)}
      <mark className="bg-amber-200 rounded px-0.5">{original}</mark>
      {context.slice(index + original.length)}
    </>
  );
}

function CorrectionPanel({ item, languageName, onApply }) {
  const [replacement, setReplacement] = useState(item.suggested_text || '');
  const [isApplying, setIsApplying] = useState(false);
  const [error, setError] = useState('');

  const apply = async () => {
    if (!window.confirm(`Replace "${item.original_text}" with "${replacement}" in the ${languageName} lyrics?`)) return;
    setIsApplying(true);
    setError('');
    try {
      await onApply(item, replacement);
    } catch (err) {
      setError(err.message);
      setIsApplying(false);
    }
  };

  return (
    <div className="mt-3 rounded-md border border-gray-200 bg-gray-50 p-3 text-sm space-y-2">
      <p className="text-xs text-gray-500">Suggested correction{languageName ? ` · ${languageName} lyrics` : ''}</p>
      {item.context && (
        <p className="whitespace-pre-wrap text-gray-700">
          <ContextLine context={item.context} original={item.original_text} />
        </p>
      )}
      <p>
        <span className="text-red-700 line-through">{item.original_text}</span>
        <span className="mx-2 text-gray-400">→</span>
        <span className="text-green-700 font-medium">{item.suggested_text}</span>
      </p>
      {item.can_apply ? (
        <div className="flex flex-wrap items-center gap-2">
          <input
            value={replacement}
            onChange={(e) => setReplacement(e.target.value)}
            aria-label="Replacement text"
            className="flex-1 min-w-[10rem] rounded-md border border-gray-300 px-2 py-1 text-sm"
          />
          <button
            onClick={apply}
            disabled={isApplying || !replacement.trim()}
            className="rounded-md bg-indigo-600 px-3 py-1.5 text-xs font-medium text-white hover:bg-indigo-700 disabled:opacity-50"
          >
            {isApplying ? 'Applying...' : 'Apply to lyrics'}
          </button>
        </div>
      ) : (
        item.status !== 'resolved' && (
          <p className="text-xs text-amber-700">
            These words aren't in the lyrics any more, or now appear more than once — edit the
            lyrics directly in the Lyrics section.
          </p>
        )
      )}
      {error && <p className="text-xs text-red-600">{error}</p>}
    </div>
  );
}

export default function FeedbackSection({ token }) {
  const [items, setItems] = useState([]);
  const [languages, setLanguages] = useState([]);
  const [filter, setFilter] = useState('');
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState('');

  const load = async () => {
    setIsLoading(true);
    setError('');
    try {
      const [feedback, languageList] = await Promise.all([
        listFeedback(token, filter),
        listLanguages(token),
      ]);
      setItems(feedback);
      setLanguages(languageList);
    } catch (err) {
      setError(err.message);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filter]);

  const changeStatus = async (item, status) => {
    try {
      await updateFeedbackStatus(token, item.feedback_id, status);
      await load();
    } catch (err) {
      setError(err.message);
    }
  };

  const applyCorrection = async (item, replacement) => {
    await applyFeedbackCorrection(token, item.feedback_id, replacement);
    await load();
  };

  const languageName = (id) => languages.find((l) => l.language_id === id)?.name || '';

  const remove = async (item) => {
    if (!window.confirm('Delete this feedback?')) return;
    try {
      await deleteFeedback(token, item.feedback_id);
      await load();
    } catch (err) {
      setError(err.message);
    }
  };

  return (
    <section>
      <div className="flex flex-wrap items-center justify-between gap-3 mb-6">
        <h2 className="text-xl font-semibold text-gray-900">Feedback</h2>
        <div className="flex flex-wrap gap-1 rounded-md bg-gray-100 p-1 text-sm">
          {[{ value: '', label: 'All' }, ...STATUSES].map((option) => (
            <button
              key={option.value || 'all'}
              onClick={() => setFilter(option.value)}
              className={`rounded px-3 py-1 ${
                filter === option.value ? 'bg-white shadow text-gray-900' : 'text-gray-600'
              }`}
            >
              {option.label}
            </button>
          ))}
        </div>
      </div>

      <ErrorBanner message={error} onRetry={load} />

      {isLoading ? (
        <p className="text-center text-gray-400 py-6">Loading...</p>
      ) : items.length === 0 ? (
        <p className="text-center text-gray-400 py-6 bg-white border border-gray-200 rounded-lg">
          No feedback{filter ? ' with this status' : ' yet'}
        </p>
      ) : (
        <ul className="space-y-3">
          {items.map((item) => (
            <li key={item.feedback_id} className="bg-white border border-gray-200 rounded-lg p-4">
              <div className="flex flex-wrap items-center gap-2 text-xs">
                <span className="rounded-full bg-gray-100 px-2 py-0.5 font-medium text-gray-700">
                  {CATEGORY_LABELS[item.category] || item.category}
                </span>
                <span className={`rounded-full px-2 py-0.5 font-medium ${STATUS_STYLES[item.status] || ''}`}>
                  {STATUSES.find((s) => s.value === item.status)?.label || item.status}
                </span>
                <span className="text-gray-500">{new Date(item.created_at).toLocaleString()}</span>
              </div>
              {!(item.original_text && item.message.startsWith(`Change "${item.original_text}" to "`)) && (
                <p className="mt-2 whitespace-pre-wrap text-sm text-gray-900">{item.message}</p>
              )}
              {item.original_text && (
                <CorrectionPanel
                  key={`${item.feedback_id}-${item.status}`}
                  item={item}
                  languageName={languageName(item.lyrics_language_id)}
                  onApply={applyCorrection}
                />
              )}
              <div className="mt-2 text-xs text-gray-500 space-y-0.5">
                {item.song_id && (
                  <p>
                    Song: {item.song_number != null ? `No. ${item.song_number} — ` : ''}
                    {item.song_title || 'deleted song'}
                  </p>
                )}
                <p>
                  From: {item.user_name ? `${item.user_name} (${item.user_mobile})` : 'deleted user'}
                </p>
              </div>
              <div className="mt-3 flex flex-wrap items-center gap-3 text-sm">
                <select
                  value={item.status}
                  onChange={(e) => changeStatus(item, e.target.value)}
                  className="rounded-md border border-gray-300 px-2 py-1 text-sm"
                  aria-label="Status"
                >
                  {STATUSES.map((s) => (
                    <option key={s.value} value={s.value}>
                      {s.label}
                    </option>
                  ))}
                </select>
                <button onClick={() => remove(item)} className="text-red-600 hover:underline">
                  Delete
                </button>
              </div>
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}
